import { useCallback, useEffect, useMemo, useState, CSSProperties } from 'react';
import { Select, Spin, Tooltip } from 'antd';
import { ShopOutlined } from '@ant-design/icons';
import { useAuth } from '../../../../store/AuthContext/AuthContext';
import { authRequest } from '../../../../common/request/authRequest';
import './LocationFilterSelect.css';

export type LocationFilterValue = {
	id: string;
	title: string;
	address?: string;
};

type LocationFilterOption = {
	id: string;
	name: string;
	address?: string;
};

type LocationFilterSelectProps = {
	value?: LocationFilterValue | null;
	onChange?: (value: LocationFilterValue | null) => void;
	placeholder?: string;
	minWidth?: number | string;
	maxWidth?: number | string;
	allowClear?: boolean;
	disabled?: boolean;
	style?: CSSProperties;
	className?: string;
};

const formatTooltip = (name: string, address?: string) =>
	address ? (
		<div>
			<div style={{ fontWeight: 600 }}>{name}</div>
			<div style={{ marginTop: 2, opacity: 0.9 }}>{address}</div>
		</div>
	) : name;

/** Extrae la población de una dirección tipo "calle, CP, población, provincia". */
const getLocality = (address?: string): string | undefined => {
	if (!address?.trim()) return undefined;
	const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
	if (parts.length === 0) return undefined;
	if (parts.length === 1) return parts[0];
	const candidate = parts.length >= 3 ? parts[parts.length - 2] : parts[parts.length - 1];
	return candidate.replace(/^\d{4,5}\s+/, '') || candidate;
};

const LocationOptionLabel = ({ name, address }: { name: string; address?: string }) => (
	<div className="location-filter-option">
		<div className="location-filter-option__name">
			<ShopOutlined className="location-filter-option__icon" />
			<span className="location-filter-option__name-text">{name}</span>
		</div>
		{address ? (
			<div className="location-filter-option__address">{address}</div>
		) : null}
	</div>
);

const SelectedOptionLabel = ({ name, address }: { name: string; address?: string }) => {
	const locality = getLocality(address);
	return (
		<span className="location-filter-selected">
			<span className="location-filter-selected__name">{name}</span>
			{locality ? (
				<span className="location-filter-selected__locality"> · {locality}</span>
			) : null}
		</span>
	);
};

/**
 * Selector/buscador de locales reutilizable.
 * GET `/projects/{projectId}/{accountId}/locations/filter`
 * → LocationFilterOption[] { id, name, address }
 * La carga se hace solo al abrir el select por primera vez.
 */
export const LocationFilterSelect = ({
	value,
	onChange,
	placeholder = 'Buscar local…',
	minWidth = 220,
	maxWidth = 280,
	allowClear = true,
	disabled = false,
	style,
	className,
}: LocationFilterSelectProps) => {
	const { projectGroup } = useAuth();
	const [options, setOptions] = useState<LocationFilterOption[]>([]);
	const [loading, setLoading] = useState(false);
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		setOptions([]);
		setLoaded(false);
		setLoading(false);
	}, [projectGroup.project, projectGroup.group]);

	const loadOptions = useCallback(() => {
		if (loaded || loading) return;

		setLoading(true);
		authRequest(
			`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/locations/filter`,
			{ method: 'GET' }
		)
			.then((data: LocationFilterOption[]) => {
				setOptions(Array.isArray(data) ? data : []);
				setLoaded(true);
			})
			.catch(() => {
				setOptions([]);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [loaded, loading, projectGroup.project, projectGroup.group]);

	const selectOptions = useMemo(() => {
		const mapped = options.map((opt) => ({
			value: opt.id,
			name: opt.name,
			address: opt.address,
			searchText: `${opt.name} ${opt.address || ''}`.toLowerCase(),
			label: <LocationOptionLabel name={opt.name} address={opt.address} />,
			selectedLabel: <SelectedOptionLabel name={opt.name} address={opt.address} />,
		}));

		if (value?.id && !mapped.some((opt) => opt.value === value.id)) {
			mapped.unshift({
				value: value.id,
				name: value.title,
				address: value.address,
				searchText: `${value.title} ${value.address || ''}`.toLowerCase(),
				label: <LocationOptionLabel name={value.title} address={value.address} />,
				selectedLabel: <SelectedOptionLabel name={value.title} address={value.address} />,
			});
		}

		return mapped;
	}, [options, value]);

	const selectedTooltip = value
		? formatTooltip(value.title, value.address)
		: undefined;

	return (
		<Tooltip
			title={selectedTooltip}
			placement="topLeft"
			mouseEnterDelay={0.35}
		>
			<span className="location-filter-select-wrap" style={{ maxWidth, display: 'inline-block', width: '100%' }}>
				<Select
					className={`location-filter-select ${className || ''}`.trim()}
					bordered={false}
					showSearch
					allowClear={allowClear}
					disabled={disabled}
					optionLabelProp="selectedLabel"
					filterOption={(input, option) =>
						String(option?.searchText || '').includes(input.trim().toLowerCase())
					}
					placeholder={placeholder}
					loading={loading}
					notFoundContent={loading ? <Spin size="small" /> : 'Sin resultados'}
					options={selectOptions}
					value={value?.id}
					listHeight={320}
					onDropdownVisibleChange={(open) => {
						if (open) loadOptions();
					}}
					onChange={(id, option) => {
						if (!id) {
							onChange?.(null);
							return;
						}
						const selected = option as { value: string; name?: string; address?: string };
						onChange?.({
							id: String(id),
							title: selected?.name || String(id),
							address: selected?.address,
						});
					}}
					style={{ minWidth, maxWidth: '100%', width: '100%', fontWeight: 500, ...style }}
					dropdownMatchSelectWidth={false}
					dropdownStyle={{ minWidth: 280, maxWidth: 360 }}
					dropdownClassName="location-filter-select-dropdown"
				/>
			</span>
		</Tooltip>
	);
};

export default LocationFilterSelect;
